import { useState, useEffect, useRef, useCallback, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react'
import useApi from '../api';
import { useParams } from 'react-router-dom';
import { formatDate } from '../utils/dateFormatter';
import Chat from '../agents/Chat';
import SessionsCard from './SessionsCardAlt';
import { useNavigate } from 'react-router-dom';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { AlertCircle, MoreHorizontal, Trash2, Ban, UserCheck, GitBranch, ChevronLeft, Clock, CalendarDays, Activity, Code, Cog, Hash, Bot, Headset, Mail, ShieldAlert, ShieldCheck, Calendar, MessageSquare, MessageSquarePlus, Sparkles, Target, User } from 'lucide-react';
import BanVisitorDialog from './BanVisitorDialog';
import BeatLoader from '../ui/BeatLoader';
import { ModerationSection, type SessionModerationData } from './SessionModeration';
import { toast } from 'react-toastify';
import Reports from '../reports/Reports';
import Assessments from '../assessments/Assessments';
import { VisitorJourney } from '../journey';
import VisitorIdentity from '../VisitorIdentity';
import CollapsibleSection from '../ui/CollapsibleSection';
import { useUser } from '../user/UserContext';
import useIsMobile from '../../hooks/useIsMobile';
import { getOutcomeDisplayLabel, type OutcomeSnapshot } from '../../lib/visitorIdentity';
import { assignedViaLabel, escalatedByLabel, escalationRepLabel, latestEscalation, type SessionEscalation } from '../agents/escalations';

interface Lead {
    id: string;
    first_name: string;
    last_name: string;
    company: string;
    email: string;
    job_title: string;
    attributes: any;
    integrations: {
        salesforce: string;
        hubspot: string;
    };
    email_verified: boolean;
    identity_verified_at?: string | null;
}

interface Message {
    id: string;
    role: string;
    content: string;
    created_at: string;
}

interface Assessment {
    id: string;
    related_assessment_id: string;
    assessment_status: string;
    assessment_responses: { [key: string]: string };
    created_at: string;
    updated_at: string;
    conversation_id: string;
    visitor_id?: string;
    lead_id?: string;
    assessment_name?: string;
}

interface Report {
    id: string;
    related_report_id: string;
    report_status: string;
    report: string;
    created_at: string;
    updated_at: string;
    conversation_id: string;
    visitor_id?: string;
    lead_id?: string;
    report_name?: string;
}

interface Agent {
    id: string;
}

interface Conversation {
    id: string;
    created_at: string;
    last_activity: string;
    processed: boolean;
    processed_at: string;
    messages: Message[];
    outcome?: OutcomeSnapshot | null;
    moderation?: SessionModerationData | null;
    interaction: boolean;
    visitor_id?: string;
    ip_address?: string;
    city: string;
    region: string;
    country: string;
    postal: string;
    status: string;
    initiated_by?: string;
    channel?: string;
    name?: string;
    lead?: Lead;
    // Session-scoped visitor profile serialized by the session detail API;
    // identity_verified_at means THIS session presented a valid signed identify.
    visitor_profile?: {
        identity_verified_at?: string | null;
    } | null;
    assessments?: Assessment[];
    reports?: Report[];
    agent?: Agent;
    agent_id?: string;
    escalations?: SessionEscalation[];
}

// removed VisitorSessionSummary; no longer rendering a table of sessions here

interface BanDetails {
    id: string;
    visitor_id?: string;
    ip_address?: string;
    reason?: string;
    banned_at: string;
    banned_by?: {
        id: string;
        name: string;
    };
    expires_at?: string;
    // 'moderation' = issued by a session review run, not a teammate.
    source?: 'user' | 'moderation';
    agent_run_id?: string | null;
}

interface BanStatus {
    is_banned: boolean;
    ban_details?: BanDetails;
}

interface OrganizationEvent {
    type: string;
    visitor_id?: string;
    conversation_id?: string;
    [key: string]: any;
}

function formatLiveUpdateTime(date: Date): string {
    return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
    });
}

const DETAILS_PANEL_WIDTH_KEY = 'session-details-panel-width';
const DEFAULT_DETAILS_PANEL_WIDTH = 420;
const MIN_DETAILS_PANEL_WIDTH = 320;
const MAX_DETAILS_PANEL_WIDTH = 720;
const MIN_CHAT_AND_SESSION_LIST_WIDTH = 640;
// Details is a fixed 320–720px column. Hide the docked panel once the
// sessions+chat strip would get crushed — same idea as agent builder
// hiding preview below 1280. Measured on the session row so a laptop
// with the Aimdoc sidebar open counts as narrow too.
const DETAILS_MIN_LAYOUT_PX = 1280;

function getInitialSessionLayoutWidth() {
    if (typeof window === 'undefined') return DETAILS_MIN_LAYOUT_PX;
    const raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--aimdoc-sidebar-width')
        .trim();
    const sidebar = Number.parseFloat(raw);
    return window.innerWidth - (Number.isFinite(sidebar) ? sidebar : 0);
}

function getMaxDetailsPanelWidth() {
    return Math.max(
        MIN_DETAILS_PANEL_WIDTH,
        Math.min(MAX_DETAILS_PANEL_WIDTH, window.innerWidth - MIN_CHAT_AND_SESSION_LIST_WIDTH),
    );
}

function clampDetailsPanelWidth(width: number) {
    return Math.min(getMaxDetailsPanelWidth(), Math.max(MIN_DETAILS_PANEL_WIDTH, width));
}

function readStoredDetailsPanelWidth() {
    try {
        const storedWidth = Number(window.localStorage.getItem(DETAILS_PANEL_WIDTH_KEY));
        if (Number.isFinite(storedWidth) && storedWidth > 0) {
            return clampDetailsPanelWidth(storedWidth);
        }
    } catch {
        // Ignore storage access failures and use the default width.
    }
    return clampDetailsPanelWidth(DEFAULT_DETAILS_PANEL_WIDTH);
}

export default function Session() {
    const { id } = useParams();
    const [modalVisible, setModalVisible] = useState(false);
    let [isOpen, setIsOpen] = useState(false);
    const { get, post, remove, connectWebSocket } = useApi();
    const { organization } = useUser();
    const [session, setSession] = useState<Conversation | null>(null);
    const [sessionLoadError, setSessionLoadError] = useState(false);
    const [message, setMessage] = useState<Message | null>(null);
    const [status, setStatus] = useState<string>('');
    const [leadId, setLeadId] = useState<string | null>(null);
    const [sessionId, setSessionId] = useState<string | null>(id || null);
    const isMobile = useIsMobile();
    const [mobilePanel, setMobilePanel] = useState<'sessions' | 'chat' | 'details'>(id ? 'chat' : 'sessions');
    const layoutRef = useRef<HTMLDivElement>(null);
    const [layoutWidth, setLayoutWidth] = useState(getInitialSessionLayoutWidth);
    const [detailsSheetOpen, setDetailsSheetOpen] = useState(false);
    const canDockDetails = !isMobile && layoutWidth >= DETAILS_MIN_LAYOUT_PX;
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [banDialogOpen, setBanDialogOpen] = useState(false);
    const [banLoading, setBanLoading] = useState(false);
    const [banStatus, setBanStatus] = useState<BanStatus | null>(null);
    const [banStatusLoading, setBanStatusLoading] = useState(false);
    const [clearingModeration, setClearingModeration] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const navigate = useNavigate();
    const [visitorSessionsTotal, setVisitorSessionsTotal] = useState<number | null>(null);
    const [visitorSessionsLoading, setVisitorSessionsLoading] = useState(false);
    const [currentTrack, setCurrentTrack] = useState<{
        track_name: string;
        track_path: string[];
        classification_source: 'logical' | 'ai';
    } | null>(null);
    const [orgEvent, setOrgEvent] = useState<OrganizationEvent | null>(null);
    const orgSocketRef = useRef<WebSocket | null>(null);
    const shouldReconnectOrgRef = useRef(true);
    const orgReconnectAttemptsRef = useRef(0);
    const orgReconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
    const orgEventSequenceRef = useRef(0);
    const [lastDetailsRefreshAt, setLastDetailsRefreshAt] = useState<Date | null>(null);
    const [detailsRefreshKey, setDetailsRefreshKey] = useState(0);
    const [detailsPanelWidth, setDetailsPanelWidth] = useState(readStoredDetailsPanelWidth);
    const detailsRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
    const detailsRefreshInFlightRef = useRef(false);
    const detailsRefreshQueuedRef = useRef(false);
    const sessionRef = useRef<Conversation | null>(session);
    const sessionIdRef = useRef<string | null>(sessionId);
    const sessionOutcomeLabel = getOutcomeDisplayLabel(session?.outcome);
    const sessionEscalation = latestEscalation(session?.escalations);
    const sessionEscalationRep = escalationRepLabel(sessionEscalation?.assigned_to);
    const sessionEscalationDetail = sessionEscalation
        ? [
            sessionEscalation.skill_name,
            assignedViaLabel(sessionEscalation.assigned_via),
            escalatedByLabel(sessionEscalation.escalated_by) && `Requested by ${escalatedByLabel(sessionEscalation.escalated_by)}`,
        ].filter(Boolean).join(' · ')
        : '';
    const contactSessionLeadId = session?.lead?.id || leadId || null;
    const relatedSessionsLabel = contactSessionLeadId ? 'Contact Sessions' : 'Visitor Sessions';
    const relatedSessionsUrl = contactSessionLeadId
        ? `/sessions?lead_id=${encodeURIComponent(contactSessionLeadId)}`
        : session?.visitor_id
            ? `/sessions?visitor_id=${encodeURIComponent(session.visitor_id)}`
            : null;

    const persistDetailsPanelWidth = useCallback((width: number) => {
        const nextWidth = clampDetailsPanelWidth(width);
        setDetailsPanelWidth(nextWidth);
        try {
            window.localStorage.setItem(DETAILS_PANEL_WIDTH_KEY, String(nextWidth));
        } catch {
            // Resizing should still work if storage is unavailable.
        }
    }, []);

    const handleDetailsResizeStart = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        event.preventDefault();
        const startX = event.clientX;
        const startWidth = detailsPanelWidth;
        let latestWidth = startWidth;
        const previousCursor = document.body.style.cursor;
        const previousUserSelect = document.body.style.userSelect;

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const handlePointerMove = (moveEvent: PointerEvent) => {
            latestWidth = clampDetailsPanelWidth(startWidth + startX - moveEvent.clientX);
            setDetailsPanelWidth(latestWidth);
        };

        const finishResize = () => {
            document.removeEventListener('pointermove', handlePointerMove);
            document.removeEventListener('pointerup', finishResize);
            document.removeEventListener('pointercancel', finishResize);
            document.body.style.cursor = previousCursor;
            document.body.style.userSelect = previousUserSelect;
            persistDetailsPanelWidth(latestWidth);
        };

        document.addEventListener('pointermove', handlePointerMove);
        document.addEventListener('pointerup', finishResize);
        document.addEventListener('pointercancel', finishResize);
    }, [detailsPanelWidth, persistDetailsPanelWidth]);

    const handleDetailsResizeKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const delta = event.key === 'ArrowLeft' ? 24 : -24;
        persistDetailsPanelWidth(detailsPanelWidth + delta);
    }, [detailsPanelWidth, persistDetailsPanelWidth]);

    useEffect(() => {
        const handleWindowResize = () => {
            setDetailsPanelWidth((currentWidth) => clampDetailsPanelWidth(currentWidth));
        };
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, []);

    useEffect(() => {
        if (!session) return;
        const el = layoutRef.current;
        if (!el) return;
        const observer = new ResizeObserver((entries) => {
            setLayoutWidth(entries[0]?.contentRect.width ?? 0);
        });
        observer.observe(el);
        setLayoutWidth(el.getBoundingClientRect().width);
        return () => observer.disconnect();
    }, [session?.id]);

    useEffect(() => {
        if (canDockDetails || isMobile) {
            setDetailsSheetOpen(false);
        }
    }, [canDockDetails, isMobile]);

    function closeModal() {
        setModalVisible(false);
        setIsOpen(false);
    }

    function openModal() {
        setIsOpen(true);
    }
    
    const fetchSessionData = useCallback(async (targetSessionId: string | null = sessionId) => {
        if (!targetSessionId) {
            return null;
        }
        try {
            const response = await get(`/sessions/${targetSessionId}`);
            const newSession = response.data;
            if (targetSessionId !== sessionIdRef.current) {
                return null;
            }
            setSession(newSession);
            setSessionLoadError(false);
            setStatus((prevStatus) => (prevStatus === newSession.status ? prevStatus : newSession.status));
            return newSession as Conversation;
        } catch (error) {
            console.error('Error fetching session:', error);
            // Only surface the failure when nothing is on screen for this
            // session yet; a failed silent refresh keeps the loaded session.
            if (targetSessionId === sessionIdRef.current && sessionRef.current?.id !== targetSessionId) {
                setSessionLoadError(true);
            }
            return null;
        }
    }, [get, sessionId]);

    useEffect(() => {
        setSessionLoadError(false);
        void fetchSessionData();
        void fetchBanStatus();
    }, [sessionId, leadId, fetchSessionData]);

    // A dialog opened for the previous session must not confirm against the
    // new one: its visitor and ip would be the old session's while the
    // request goes to the new session id.
    useEffect(() => {
        setPopoverOpen(false);
        setBanDialogOpen(false);
        setDeleteDialogOpen(false);
    }, [sessionId]);

    useEffect(() => {
        if (id) {
            setSessionId(id);
        }
    }, [id]);

    useEffect(() => {
        if (message) {
            openModal();
        }
    }, [message]);

    const handleDeleteSession = () => {
        setPopoverOpen(false);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!sessionId) return;
        
        setDeleteLoading(true);
        try {
            await remove(`/sessions/${sessionId}`);
            toast.success('Session deleted successfully');
            setDeleteDialogOpen(false);
            navigate('/sessions');
        } catch (error) {
            console.error('Error deleting session:', error);
            toast.error('Failed to delete session. Please try again.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleBanVisitor = () => {
        setPopoverOpen(false);
        setBanDialogOpen(true);
    };

    const fetchBanStatus = async () => {
        if (!sessionId) return;
        
        setBanStatusLoading(true);
        try {
            const response = await get(`/sessions/${sessionId}/ban-status`);
            setBanStatus(response.data);
        } catch (error) {
            console.error('Error fetching ban status:', error);
            setBanStatus({ is_banned: false });
        } finally {
            setBanStatusLoading(false);
        }
    };

    const handleConfirmBan = async (reason: string) => {
        if (!session || session.id !== sessionId) return;
        
        setBanLoading(true);
        try {
            await post(`/sessions/${sessionId}/ban-visitor`, {
                visitor_id: session.visitor_id,
                ip_address: session.ip_address,
                reason: reason || undefined
            });
            
            toast.success('Visitor has been banned successfully');
            setBanDialogOpen(false);
            // Refresh ban status
            await fetchBanStatus();
        } catch (error) {
            console.error('Error banning visitor:', error);
            toast.error('Failed to ban visitor. Please try again.');
        } finally {
            setBanLoading(false);
        }
    };

    const handleClearModeration = async () => {
        if (!sessionId) return;

        setClearingModeration(true);
        try {
            await post(`/sessions/${sessionId}/moderation/clear`, {});
            toast.success('Flag cleared');
            await fetchSessionData();
        } catch (error) {
            console.error('Error clearing moderation flag:', error);
            toast.error('Failed to clear the flag. Please try again.');
        } finally {
            setClearingModeration(false);
        }
    };

    const handleUnbanVisitor = async () => {
        if (!sessionId) return;
        
        setPopoverOpen(false);
        setBanStatusLoading(true);
        try {
            await remove(`/sessions/${sessionId}/ban-visitor`);
            toast.success('Visitor has been unbanned successfully');
            // Refresh ban status, and the moderation state the unban lifted
            await fetchBanStatus();
            await fetchSessionData();
        } catch (error) {
            console.error('Error unbanning visitor:', error);
            toast.error('Failed to unban visitor. Please try again.');
        } finally {
            setBanStatusLoading(false);
        }
    };

    type FetchVisitorSessionsOptions = {
        silent?: boolean;
    };

    const fetchRelatedSessions = useCallback(async (
        scope: { leadId?: string | null; visitorId?: string | null },
        options: FetchVisitorSessionsOptions = {}
    ) => {
        const shouldShowLoader = !options.silent;
        if (shouldShowLoader) {
            setVisitorSessionsLoading(true);
        }
        try {
            const params = new URLSearchParams({ per_page: '1' });
            if (scope.leadId) {
                params.set('lead_id', scope.leadId);
            } else if (scope.visitorId) {
                params.set('visitor_id', scope.visitorId);
            } else {
                setVisitorSessionsTotal(null);
                return;
            }
            // We only need the total count; minimize payload
            const response = await get(`/sessions/?${params.toString()}`);
            const total = response.data?.total ?? response.data?.total_sessions ?? null;
            setVisitorSessionsTotal(typeof total === 'number' ? total : null);
        } catch (error) {
            console.error('Error fetching related sessions:', error);
            setVisitorSessionsTotal(null);
        } finally {
            if (shouldShowLoader) {
                setVisitorSessionsLoading(false);
            }
        }
    }, [get]);

    const fetchCurrentTrack = useCallback(async (visitorId: string, agentId?: string) => {
        try {
            const response = await get(`/events?visitor_id=${encodeURIComponent(visitorId)}`);
            const events = response.data.events || [];
            
            // Find the latest track_classification event
            const trackEvents = events.filter((e: any) => e.event_type === 'track_classification');
            const scopedTrackEvents = agentId
                ? trackEvents.filter((e: any) => String(e?.payload?.agent_id) === String(agentId))
                : trackEvents;

            if (scopedTrackEvents.length > 0) {
                // Events come sorted by created_at desc, so first one is the latest
                const latestTrack = scopedTrackEvents[0];
                const trackPath = Array.isArray(latestTrack?.payload?.track_path)
                    ? latestTrack.payload.track_path
                    : [];
                setCurrentTrack({
                    track_name: latestTrack.payload.track_name,
                    track_path: trackPath,
                    classification_source: latestTrack.payload.classification_source,
                });
            } else {
                setCurrentTrack(null);
            }
        } catch (error) {
            console.error('Error fetching current track:', error);
            setCurrentTrack(null);
        }
    }, [get]);

    useEffect(() => {
        sessionRef.current = session;
    }, [session]);

    useEffect(() => {
        sessionIdRef.current = sessionId;
    }, [sessionId]);

    const runSilentDetailsRefresh = useCallback(async () => {
        if (detailsRefreshInFlightRef.current) {
            detailsRefreshQueuedRef.current = true;
            return;
        }

        detailsRefreshInFlightRef.current = true;
        try {
            do {
                detailsRefreshQueuedRef.current = false;
                const activeSessionId = sessionIdRef.current;
                if (!activeSessionId) {
                    break;
                }

                const refreshedSession = await fetchSessionData(activeSessionId);
                const effectiveSession = refreshedSession ?? sessionRef.current;
                const visitorId = effectiveSession?.visitor_id;
                const effectiveLeadId = effectiveSession?.lead?.id || leadId || null;
                const agentId = effectiveSession?.agent_id || effectiveSession?.agent?.id;

                if (visitorId) {
                    await Promise.all([
                        fetchRelatedSessions({ leadId: effectiveLeadId, visitorId }, { silent: true }),
                        fetchCurrentTrack(visitorId, agentId),
                    ]);
                }

                setDetailsRefreshKey((prev) => prev + 1);
                setLastDetailsRefreshAt(new Date());
            } while (detailsRefreshQueuedRef.current);
        } finally {
            detailsRefreshInFlightRef.current = false;
        }
    }, [fetchCurrentTrack, fetchRelatedSessions, fetchSessionData, leadId]);

    const scheduleSilentDetailsRefresh = useCallback((_reason?: string) => {
        if (detailsRefreshTimerRef.current) {
            clearTimeout(detailsRefreshTimerRef.current);
        }
        detailsRefreshTimerRef.current = setTimeout(() => {
            detailsRefreshTimerRef.current = null;
            void runSilentDetailsRefresh();
        }, 350);
    }, [runSilentDetailsRefresh]);

    useEffect(() => {
        if (contactSessionLeadId || session?.visitor_id) {
            fetchRelatedSessions({
                leadId: contactSessionLeadId,
                visitorId: session?.visitor_id,
            });
        } else {
            setVisitorSessionsTotal(null);
        }
    }, [contactSessionLeadId, fetchRelatedSessions, session?.visitor_id]);

    useEffect(() => {
        const agentId = session?.agent_id || session?.agent?.id;
        if (session?.visitor_id) {
            fetchCurrentTrack(session.visitor_id, agentId);
        } else {
            setCurrentTrack(null);
        }
    }, [fetchCurrentTrack, session?.agent?.id, session?.agent_id, session?.visitor_id]);

    useEffect(() => {
        if (!organization?.id) {
            return;
        }

        shouldReconnectOrgRef.current = true;
        orgReconnectAttemptsRef.current = 0;
        if (orgReconnectTimerRef.current) {
            clearTimeout(orgReconnectTimerRef.current);
            orgReconnectTimerRef.current = null;
        }

        const scheduleReconnect = () => {
            if (!shouldReconnectOrgRef.current || orgReconnectTimerRef.current) return;
            const attempts = orgReconnectAttemptsRef.current;
            const delay = Math.min(1000 * 2 ** attempts, 30000);
            orgReconnectTimerRef.current = setTimeout(() => {
                orgReconnectTimerRef.current = null;
                orgReconnectAttemptsRef.current = attempts + 1;
                void initializeOrgWebSocket();
            }, delay);
        };

        const initializeOrgWebSocket = async () => {
            try {
                const ws = await connectWebSocket(`/ws/organization/${organization.id}`);
                if (!ws) {
                    console.error('Organization WebSocket connection failed');
                    scheduleReconnect();
                    return;
                }

                orgSocketRef.current = ws;
                orgReconnectAttemptsRef.current = 0;

                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data) as OrganizationEvent;
                        if (
                            data.type === 'session_active' ||
                            data.type === 'session_inactive' ||
                            data.type === 'visitor_active' ||
                            data.type === 'visitor_delta_update' ||
                            data.type === 'visitor_page_change' ||
                            data.type === 'visitor_inactive'
                        ) {
                            orgEventSequenceRef.current += 1;
                            setOrgEvent({
                                ...data,
                                _sequence: orgEventSequenceRef.current,
                            });
                        }
                    } catch (error) {
                        console.error('Error parsing organization event:', error);
                    }
                };

                ws.onclose = (closeEvent) => {
                    if (orgSocketRef.current === ws) {
                        orgSocketRef.current = null;
                    }
                    if (shouldReconnectOrgRef.current && closeEvent.code !== 1000 && closeEvent.code !== 1008) {
                        scheduleReconnect();
                    }
                };

                ws.onerror = (error) => {
                    console.error('Organization WebSocket error:', error);
                };
            } catch (error) {
                console.error('Failed to establish organization WebSocket connection:', error);
                scheduleReconnect();
            }
        };

        void initializeOrgWebSocket();

        return () => {
            shouldReconnectOrgRef.current = false;
            if (orgReconnectTimerRef.current) {
                clearTimeout(orgReconnectTimerRef.current);
                orgReconnectTimerRef.current = null;
            }
            if (orgSocketRef.current?.readyState === WebSocket.OPEN) {
                orgSocketRef.current.send(JSON.stringify({ type: 'silent_close_connection' }));
                orgSocketRef.current.close();
            }
            orgSocketRef.current = null;
        };
    }, [organization?.id, connectWebSocket]);

    useEffect(() => {
        return () => {
            if (detailsRefreshTimerRef.current) {
                clearTimeout(detailsRefreshTimerRef.current);
                detailsRefreshTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        setLastDetailsRefreshAt(null);
        setDetailsRefreshKey(0);
    }, [sessionId]);

    useEffect(() => {
        if (!orgEvent?.type || !session?.visitor_id) {
            return;
        }
        if (orgEvent.visitor_id !== session.visitor_id) {
            return;
        }

        if (orgEvent.type === 'session_active' || orgEvent.type === 'session_inactive') {
            scheduleSilentDetailsRefresh(orgEvent.type);
        }
    }, [orgEvent, scheduleSilentDetailsRefresh, session?.visitor_id]);

    // Sections injected into VisitorIdentity's panel-variant slots so the whole
    // details panel reads as one ordered accordion (see slot props below).
    // Order: contact summary → collected fields → [sessionMetaSlot] → browsing →
    // [behaviorSlot] → technical context → related profiles → [banSlot].
    const sessionMetaSlot = session ? (
        <>
            <CollapsibleSection
                title="Session Details"
                icon={<Activity className="w-4 h-4 text-muted-foreground" />}
                storageKey="session-details:Session Details"
            >
                <div className="divide-y">
                    {session.visitor_id && !contactSessionLeadId && (
                        <>
                            <div className="py-2.5 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2 shrink-0">
                                    <Hash className="w-3.5 h-3.5 text-muted-foreground" />
                                    <span className="text-sm font-medium text-muted-foreground">Visitor ID</span>
                                </div>
                                <span className="text-xs truncate text-muted-foreground font-mono">{session.visitor_id}</span>
                            </div>
                            <div className="py-2.5 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2 shrink-0">
                                    <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
                                    <span className="text-sm font-medium text-muted-foreground">{relatedSessionsLabel}</span>
                                </div>
                                {visitorSessionsLoading ? (
                                    <span className="text-sm text-muted-foreground">Loading…</span>
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm">
                                            {visitorSessionsTotal !== null ? `${visitorSessionsTotal} total` : '–'}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-6 text-xs px-2"
                                            disabled={!relatedSessionsUrl}
                                            onClick={() => relatedSessionsUrl && navigate(relatedSessionsUrl)}
                                        >
                                            View
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    {contactSessionLeadId && (
                        <div className="py-2.5 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 shrink-0">
                                <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
                                <span className="text-sm font-medium text-muted-foreground">{relatedSessionsLabel}</span>
                            </div>
                            {visitorSessionsLoading ? (
                                <span className="text-sm text-muted-foreground">Loading…</span>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <span className="text-sm">
                                        {visitorSessionsTotal !== null ? `${visitorSessionsTotal} total` : '–'}
                                    </span>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-6 text-xs px-2"
                                        disabled={!relatedSessionsUrl}
                                        onClick={() => relatedSessionsUrl && navigate(relatedSessionsUrl)}
                                    >
                                        View
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="py-2.5 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2 shrink-0">
                            <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="text-sm font-medium text-muted-foreground">Started</span>
                        </div>
                        <span className="text-sm">{formatDate(session.created_at, 'short')}</span>
                    </div>
                    <div className="py-2.5 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2 shrink-0">
                            <MessageSquarePlus className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="text-sm font-medium text-muted-foreground">Started By</span>
                        </div>
                        {session.initiated_by === 'ai' ? (
                            <Badge variant="outline" className="shrink-0 text-xs gap-1 border-purple-500/50 text-purple-500">
                                <Sparkles className="w-3 h-3" /> AI
                            </Badge>
                        ) : session.initiated_by === 'human' ? (
                            <Badge variant="outline" className="shrink-0 text-xs gap-1 border-emerald-500/50 text-emerald-500">
                                <Headset className="w-3 h-3" /> Rep
                            </Badge>
                        ) : session.initiated_by === 'sdk' ? (
                            <Badge variant="outline" className="shrink-0 text-xs gap-1 border-orange-500/50 text-orange-500">
                                <Code className="w-3 h-3" /> SDK
                            </Badge>
                        ) : session.initiated_by === 'ai_agent' ? (
                            <Badge variant="outline" className="shrink-0 text-xs gap-1 border-cyan-500/50 text-cyan-500">
                                <Bot className="w-3 h-3" /> AI Agent
                            </Badge>
                        ) : (
                            <Badge variant="outline" className="shrink-0 text-xs gap-1 border-muted-foreground/50 text-muted-foreground">
                                <User className="w-3 h-3" /> Visitor
                            </Badge>
                        )}
                    </div>
                    {(session.channel === 'email' || session.channel === 'gateway') && (
                        <div className="py-2.5 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 shrink-0">
                                <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                                <span className="text-sm font-medium text-muted-foreground">Channel</span>
                            </div>
                            {session.channel === 'email' ? (
                                <Badge variant="outline" className="shrink-0 text-xs gap-1 border-amber-500/50 text-amber-500">
                                    <Mail className="w-3 h-3" /> Email Thread
                                </Badge>
                            ) : (
                                <Badge variant="outline" className="shrink-0 text-xs gap-1 border-cyan-500/50 text-cyan-500">
                                    <Bot className="w-3 h-3" /> Agent Gateway
                                </Badge>
                            )}
                        </div>
                    )}
                    {session.last_activity && (
                        <div className="py-2.5 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 shrink-0">
                                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                                <span className="text-sm font-medium text-muted-foreground">Last Active</span>
                            </div>
                            <span className="text-sm">{formatDate(session.last_activity, 'short')}</span>
                        </div>
                    )}
                    {session.visitor_profile?.identity_verified_at && (
                        <div className="py-2.5 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 shrink-0">
                                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                                <span className="text-sm font-medium text-muted-foreground">Identity</span>
                            </div>
                            <span
                                className="text-sm text-emerald-600 dark:text-emerald-400"
                                title="Identity verified this session"
                            >
                                Verified
                            </span>
                        </div>
                    )}
                    {sessionOutcomeLabel && (
                        <div className="py-2.5 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 shrink-0">
                                <Target className="w-3.5 h-3.5 text-violet-500" />
                                <span className="text-sm font-medium text-muted-foreground">Outcome</span>
                            </div>
                            <div className="min-w-0 flex items-center gap-2">
                                <span className="text-sm truncate" title={sessionOutcomeLabel}>
                                    {sessionOutcomeLabel}
                                </span>
                            </div>
                        </div>
                    )}
                    {sessionEscalation && (
                        <div className="py-2.5 flex items-start justify-between gap-4">
                            <div className="flex items-center gap-2 shrink-0">
                                <Headset
                                    className={sessionEscalation.status === 'missed'
                                        ? "w-3.5 h-3.5 text-amber-500"
                                        : "w-3.5 h-3.5 text-muted-foreground"}
                                />
                                <span className="text-sm font-medium text-muted-foreground">Escalation</span>
                            </div>
                            <div className="min-w-0 flex flex-col items-end text-right">
                                <span
                                    className="text-sm truncate max-w-full"
                                    title={sessionEscalation.assigned_to?.email || undefined}
                                >
                                    {sessionEscalation.status === 'missed'
                                        ? 'Missed, no reps available'
                                        : sessionEscalationRep
                                            ? `Escalated to ${sessionEscalationRep}`
                                            : 'Escalated, no rep assigned'}
                                </span>
                                <span className="text-xs text-muted-foreground truncate max-w-full">
                                    {[
                                        sessionEscalationDetail,
                                        sessionEscalation.created_at && formatDate(sessionEscalation.created_at, 'short'),
                                    ].filter(Boolean).join(' · ')}
                                </span>
                            </div>
                        </div>
                    )}
                    {session.processed && (
                        <div className="py-2.5 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 shrink-0">
                                <Cog className="w-3.5 h-3.5 text-muted-foreground" />
                                <span className="text-sm font-medium text-muted-foreground">Processed</span>
                            </div>
                            <span className="text-sm">Yes</span>
                        </div>
                    )}
                    {session.processed_at && (
                        <div className="py-2.5 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2 shrink-0">
                                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                                <span className="text-sm font-medium text-muted-foreground">Last Processed</span>
                            </div>
                            <span className="text-sm">{formatDate(session.processed_at, 'short')}</span>
                        </div>
                    )}
                </div>
            </CollapsibleSection>

            {currentTrack && (
                <CollapsibleSection
                    title="Current Track"
                    icon={<GitBranch className="w-4 h-4 text-violet-500" />}
                    storageKey="session-details:Current Track"
                >
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                            {currentTrack.track_path.map((trackName, index) => (
                                <span key={index} className="flex items-center">
                                    {index > 0 && <span className="mx-1 text-muted-foreground">→</span>}
                                    <span className={`text-sm ${index === currentTrack.track_path.length - 1 ? 'font-semibold text-violet-500' : 'text-muted-foreground'}`}>
                                        {trackName}
                                    </span>
                                </span>
                            ))}
                        </div>
                        <Badge
                            variant="outline"
                            className={`shrink-0 text-xs gap-1 ${currentTrack.classification_source === 'ai' ? 'border-blue-500/50 text-blue-500' : 'border-muted-foreground/50 text-muted-foreground'}`}
                        >
                            {currentTrack.classification_source === 'ai' ? <><Bot className="w-3 h-3" /> AI</> : <><Cog className="w-3 h-3" /> Logical</>}
                        </Badge>
                    </div>
                </CollapsibleSection>
            )}
        </>
    ) : null;

    const behaviorSlot = session ? (
        <>
            {(contactSessionLeadId || session.visitor_id) && (
                <CollapsibleSection
                    title="Visitor Journey"
                    icon={<Clock className="w-4 h-4 text-muted-foreground" />}
                    storageKey="session-details:Visitor Journey"
                >
                    <VisitorJourney
                        key={`visitor-journey-${contactSessionLeadId || session.visitor_id}-${detailsRefreshKey}`}
                        leadId={contactSessionLeadId || undefined}
                        visitorId={contactSessionLeadId ? undefined : session.visitor_id}
                        embedded
                    />
                </CollapsibleSection>
            )}

            <Assessments
                assessments={session.assessments || []}
                onAssessmentDeleted={fetchSessionData}
                compact={true}
                variant="panel"
                sectionStateKey="session-details"
            />

            <Reports
                reports={session.reports || []}
                onReportDeleted={fetchSessionData}
                compact={true}
                variant="panel"
                sectionStateKey="session-details"
            />
        </>
    ) : null;

    const moderationSlot = session?.moderation ? (
        <ModerationSection
            moderation={session.moderation}
            onClear={handleClearModeration}
            clearing={clearingModeration}
        />
    ) : null;

    const banInfoSlot = session && banStatus?.is_banned && banStatus.ban_details ? (
        <CollapsibleSection
            title="Ban Information"
            icon={<ShieldAlert className="w-4 h-4 text-red-500" />}
            storageKey="session-details:Ban Information"
        >
            <div className="divide-y">
                <div className="py-2.5 flex items-center justify-between gap-4">
                    <span className="text-sm font-medium text-muted-foreground">Status</span>
                    <Badge variant="destructive" className="text-xs">Banned</Badge>
                </div>
                <div className="py-2.5 flex items-center justify-between gap-4">
                    <span className="text-sm font-medium text-muted-foreground">Banned At</span>
                    <span className="text-sm">{formatDate(banStatus.ban_details.banned_at)}</span>
                </div>
                {banStatus.ban_details.source === 'moderation' ? (
                    <div className="py-2.5 flex items-center justify-between gap-4">
                        <span className="text-sm font-medium text-muted-foreground">Banned By</span>
                        <span className="text-sm">Session moderation</span>
                    </div>
                ) : banStatus.ban_details.banned_by && (
                    <div className="py-2.5 flex items-center justify-between gap-4">
                        <span className="text-sm font-medium text-muted-foreground">Banned By</span>
                        <span className="text-sm">{banStatus.ban_details.banned_by.name}</span>
                    </div>
                )}
                {banStatus.ban_details.reason && (
                    <div className="py-2.5 flex items-center justify-between gap-4">
                        <span className="text-sm font-medium text-muted-foreground">Reason</span>
                        <span className="text-sm">{banStatus.ban_details.reason}</span>
                    </div>
                )}
                {banStatus.ban_details.expires_at && (
                    <div className="py-2.5 flex items-center justify-between gap-4">
                        <span className="text-sm font-medium text-muted-foreground">Expires At</span>
                        <span className="text-sm">{formatDate(banStatus.ban_details.expires_at)}</span>
                    </div>
                )}
            </div>
        </CollapsibleSection>
    ) : null;

    const banSlot = moderationSlot || banInfoSlot ? (
        <>
            {moderationSlot}
            {banInfoSlot}
        </>
    ) : null;

    const showDockedDetails = canDockDetails;
    // The sidebar moves its highlight the moment a row is clicked, but
    // `session` is still the previous one until /sessions/{id} answers. The
    // chat and details columns show a loader for that window instead of the
    // old session, so a slow switch reads as loading rather than ignored.
    const sessionReady = !!session && session.id === sessionId;
    const sessionPlaceholder = sessionLoadError ? (
        <div role="alert" className="flex flex-1 flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
            <AlertCircle className="h-5 w-5" />
            <span>This session could not be loaded.</span>
            <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                    setSessionLoadError(false);
                    void fetchSessionData();
                }}
            >
                Try again
            </Button>
        </div>
    ) : (
        <div role="status" aria-label="Loading session" className="flex flex-1 flex-col items-center justify-center">
            <BeatLoader />
        </div>
    );
    const showMobileDetails = isMobile && mobilePanel === 'details';
    const detailsBody = (opts?: { showBack?: boolean; headerClassName?: string }) => (
        <>
            <div className={`flex items-center justify-between p-4 md:p-6 border-b border-border dark:border-gray-700 ${opts?.headerClassName || ''}`}>
                <div className="flex items-center gap-3">
                    {opts?.showBack && (
                        <button onClick={() => setMobilePanel('chat')} className="p-1 -ml-1 text-muted-foreground hover:text-foreground">
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                    )}
                    <h1 className="text-xl font-semibold dark:text-slate-100">Details</h1>
                    {lastDetailsRefreshAt && (
                        <span className="text-xs text-muted-foreground">
                            Live update: {formatLiveUpdateTime(lastDetailsRefreshAt)}
                        </span>
                    )}
                </div>
                {sessionReady && (
                <div className="flex items-center gap-1">
                    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                        <PopoverTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Open menu</span>
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-48" align="end">
                            <div className="grid gap-1">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="justify-start text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                                    onClick={handleDeleteSession}
                                >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete Session
                                </Button>
                                {banStatus?.is_banned ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="justify-start text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950"
                                        onClick={handleUnbanVisitor}
                                        disabled={banStatusLoading}
                                    >
                                        <UserCheck className="mr-2 h-4 w-4" />
                                        Unban Visitor
                                    </Button>
                                ) : (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="justify-start text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                                        onClick={handleBanVisitor}
                                    >
                                        <Ban className="mr-2 h-4 w-4" />
                                        Ban Visitor
                                    </Button>
                                )}
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
                )}
            </div>
            <div className="flex min-w-0 flex-col px-3 py-4 md:px-4">
                {sessionReady ? (
                    <VisitorIdentity
                        key={`visitor-identity-${session.id}-${detailsRefreshKey}`}
                        conversationId={session.id}
                        lead={session.lead}
                        mode="full"
                        variant="panel"
                        sectionStateKey="session-details"
                        slotAfterSummary={banSlot}
                        slotAfterCollected={sessionMetaSlot}
                        slotAfterBrowsing={behaviorSlot}
                    />
                ) : (
                    <div className="py-16">{sessionPlaceholder}</div>
                )}
            </div>
        </>
    );

    return (
        <>
            {sessionId && (
                <div ref={layoutRef} className="flex w-full max-h-screen overflow-y-auto overflow-x-hidden">
                    <div className={isMobile && mobilePanel !== 'sessions' ? 'hidden' : 'contents'}>
                        <SessionsCard
                            setSessionId={(id: string) => {
                                setSessionId(id);
                                if (isMobile) setMobilePanel('chat');
                            }}
                            selectedSessionId={sessionId}
                            selectedSessionStatus={status}
                            setSelectedSessionStatus={setStatus}
                            orgEvent={orgEvent}
                            onNavigateHome={() => navigate('/sessions')}
                        />
                    </div>
                    <div className={isMobile && mobilePanel !== 'chat' ? 'hidden' : 'contents'}>
                        {!sessionReady ? (
                            <div className="flex h-screen w-full min-w-0 flex-col overflow-x-hidden">
                                {sessionPlaceholder}
                            </div>
                        ) : (
                        <Chat
                            chatId={session.id}
                            initialConversation={session}
                            status={status}
                            setStatus={setStatus}
                            initiatedBy={session.initiated_by}
                            channel={session.channel}
                            emailSubject={session.channel === 'email' ? session.name : undefined}
                            orgEvent={orgEvent}
                            onDetailsRefreshHint={scheduleSilentDetailsRefresh}
                            onBackToSessions={() => setMobilePanel('sessions')}
                            onShowDetails={
                                canDockDetails
                                    ? undefined
                                    : () => (isMobile ? setMobilePanel('details') : setDetailsSheetOpen(true))
                            }
                        />
                        )}
                    </div>

                    {showDockedDetails && (
                        <div
                            role="separator"
                            aria-label="Resize session details"
                            aria-orientation="vertical"
                            aria-valuemin={MIN_DETAILS_PANEL_WIDTH}
                            aria-valuemax={getMaxDetailsPanelWidth()}
                            aria-valuenow={Math.round(detailsPanelWidth)}
                            tabIndex={0}
                            onPointerDown={handleDetailsResizeStart}
                            onKeyDown={handleDetailsResizeKeyDown}
                            className="group relative z-30 w-px shrink-0 cursor-col-resize touch-none bg-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            title="Drag to resize session details"
                        >
                            <span className="absolute inset-y-0 left-1/2 w-3 -translate-x-1/2" />
                            <span className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-transparent transition-colors duration-200 group-hover:bg-muted-foreground/25 group-focus-visible:bg-muted-foreground/35" />
                        </div>
                    )}
                    {(showDockedDetails || showMobileDetails) && (
                        <div
                            className={`${isMobile ? 'w-full' : ''} min-w-0 h-full overflow-y-auto overflow-x-hidden scrollbar-thin`}
                            style={isMobile ? undefined : { width: detailsPanelWidth, flex: '0 0 auto' }}
                        >
                            {detailsBody({ showBack: isMobile })}
                        </div>
                    )}
                </div>
            )}

            <Sheet open={detailsSheetOpen} onOpenChange={setDetailsSheetOpen}>
                <SheetContent
                    side="right"
                    className="flex h-full w-full flex-col gap-0 overflow-y-auto overflow-x-hidden p-0 sm:max-w-[420px]"
                    aria-describedby={undefined}
                >
                    <SheetTitle className="sr-only">Details</SheetTitle>
                    {detailsBody({ headerClassName: 'pr-12' })}
                </SheetContent>
            </Sheet>

            <BanVisitorDialog
                open={banDialogOpen}
                onOpenChange={setBanDialogOpen}
                visitorId={session?.visitor_id}
                ipAddress={session?.ip_address}
                onConfirm={handleConfirmBan}
                loading={banLoading}
            />

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Session</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this session? This action cannot be undone.
                            All messages and data associated with this session will be permanently removed.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteDialogOpen(false)}
                            disabled={deleteLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleConfirmDelete}
                            disabled={deleteLoading}
                        >
                            {deleteLoading ? 'Deleting...' : 'Delete Session'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
